import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useList, useInvalidate } from "@/lib/hooks";
import api, { apiError } from "@/lib/api";
import { PageHeader, Panel, StatCard, EmptyState, Field, money } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CurrencyDollar, TrendUp, Target, ChartLineUp, SignIn, SignOut, CheckCircle, Clock, CalendarCheck, Ticket, Plus, Sparkle, HourglassMedium, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { MySalarySlips } from "@/components/hr/SalarySlips";

function Ring({ percent }) {
  const p = Math.min(100, Math.max(0, percent || 0));
  const r = 42, c = 2 * Math.PI * r;
  return (
    <svg width="110" height="110" viewBox="0 0 110 110" className="shrink-0">
      <circle cx="55" cy="55" r={r} fill="none" stroke="#e2e8f0" strokeWidth="10" />
      <circle cx="55" cy="55" r={r} fill="none" stroke="#2563eb" strokeWidth="10" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c - (c * p) / 100} transform="rotate(-90 55 55)" />
      <text x="55" y="60" textAnchor="middle" className="fill-slate-900 font-bold" fontSize="20">{Math.round(p)}%</text>
    </svg>
  );
}

function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); } catch { return "—"; }
}

const TICKET_TYPES = [
  { value: "leave", label: "Leave Request" },
  { value: "appraisal", label: "Appraisal Request" },
  { value: "concern", label: "Raise a Concern" },
  { value: "other", label: "Other" },
];
const TICKET_STATUS_STYLE = {
  open: "bg-amber-50 text-amber-700 ring-amber-200",
  approved: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  rejected: "bg-red-50 text-red-700 ring-red-200",
  resolved: "bg-blue-50 text-blue-700 ring-blue-200",
};

function CommissionCard({ icon: Icon, label, value, note, tone }) {
  const tones = {
    blue: "from-blue-500 to-blue-700",
    amber: "from-amber-400 to-amber-600",
    red: "from-rose-500 to-red-600",
  };
  return (
    <Panel className="p-5 relative overflow-hidden" data-testid={`commission-${tone}`}>
      <div className={`absolute -right-6 -top-6 w-24 h-24 rounded-full bg-gradient-to-br ${tones[tone]} opacity-10`} />
      <div className="flex items-center gap-2 text-slate-500"><Icon size={18} weight="duotone" /><span className="text-sm font-semibold">{label}</span></div>
      <p className="text-3xl font-heading font-extrabold text-slate-900 mt-3 tabular">{money(value)}</p>
      <p className="text-xs text-slate-400 mt-1">{note}</p>
    </Panel>
  );
}

function TicketsPanel() {
  const { data: tickets = [] } = useList("my-tickets", "/tickets", { refetchInterval: 30000 });
  const invalidate = useInvalidate();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ type: "leave", subject: "", description: "", start_date: "", end_date: "" });

  const submit = async () => {
    if (!form.subject.trim()) { toast.error("Please add a subject"); return; }
    setSaving(true);
    try {
      await api.post("/tickets", form);
      toast.success("Ticket submitted to Admin");
      setOpen(false);
      setForm({ type: "leave", subject: "", description: "", start_date: "", end_date: "" });
      invalidate(["my-tickets"]);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const inp = "bg-white border-slate-300";
  return (
    <Panel className="p-5" data-testid="tickets-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2"><Ticket size={18} className="text-blue-600" weight="duotone" />My Tickets</h3>
        <Button onClick={() => setOpen(true)} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold h-9" data-testid="new-ticket-btn"><Plus size={16} className="mr-1" />Submit Ticket</Button>
      </div>
      {tickets.length === 0 ? (
        <EmptyState icon={Ticket} title="No tickets yet" hint="Submit a leave, appraisal or concern ticket — it goes straight to Admin." />
      ) : (
        <div className="divide-y divide-slate-100">
          {tickets.map((t) => (
            <div key={t.id} className="py-3" data-testid={`ticket-${t.id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 truncate">{t.subject}</p>
                  <p className="text-xs text-slate-400 capitalize">{t.type}{t.start_date ? ` · ${t.start_date}${t.end_date ? ` → ${t.end_date}` : ""}` : ""}</p>
                </div>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ring-1 ring-inset capitalize ${TICKET_STATUS_STYLE[t.status] || "bg-slate-100 text-slate-700 ring-slate-200"}`}>{t.status}</span>
              </div>
              {t.description && <p className="text-sm text-slate-600 mt-1">{t.description}</p>}
              {t.admin_response && <p className="text-xs text-blue-600 mt-1 bg-blue-50 rounded-md px-2 py-1"><b>Admin:</b> {t.admin_response}</p>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Submit a Ticket</DialogTitle><DialogDescription className="sr-only">Submit a request to admin</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <Field label="Type">
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className={inp} data-testid="ticket-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TICKET_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Subject *"><Input data-testid="ticket-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={inp} placeholder="Short summary" /></Field>
            {form.type === "leave" && (
              <div className="grid grid-cols-2 gap-3">
                <Field label="From"><Input type="date" data-testid="ticket-start" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className={inp} /></Field>
                <Field label="To"><Input type="date" data-testid="ticket-end" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className={inp} /></Field>
              </div>
            )}
            <Field label="Details"><Textarea data-testid="ticket-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inp} rows={3} placeholder="Add any context for the admin…" /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-300 bg-white text-slate-700">Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold" data-testid="ticket-submit-btn">{saving ? "Submitting…" : "Submit"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Panel>
  );
}

export default function MyActivity() {
  const { user } = useAuth();
  const isSales = user.role === "sales";
  const { data: act, isLoading } = useList("my-activity", "/sales/my-activity", { refetchInterval: 30000 });
  const invalidate = useInvalidate();
  const today = act?.attendance?.today;

  const mark = async (kind) => {
    try {
      await api.post(`/attendance/${kind}`);
      window.dispatchEvent(new Event("shift-refresh"));
      invalidate(["my-activity"]);
      toast.success(kind === "check-in" ? "Checked in for today" : "Checked out");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (isLoading || !act) return <div className="text-slate-400">Loading activity…</div>;
  const t = act.totals || {};
  const fc = act.flat_commission || { projected: 0, available: 0, lost: 0, rate: 100 };

  return (
    <div>
      <PageHeader title="My Activity" subtitle={isSales ? "Your commission, attendance, score and requests" : "Your attendance and requests"} />

      {/* Commission tracker (sales) */}
      {isSales && (
        <>
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Commission Tracker · ${fc.rate} per lead</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <CommissionCard icon={Sparkle} tone="blue" label="Projected Commission" value={fc.projected} note={`${t.total || 0} leads submitted × $${fc.rate}`} />
            <CommissionCard icon={HourglassMedium} tone="amber" label="Available Commission" value={fc.available} note="Leads moved to Dispatch" />
            <CommissionCard icon={XCircle} tone="red" label="Lost Commission" value={fc.lost} note={`${t.rejected || 0} rejected leads`} />
          </div>
        </>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Panel className="p-5 lg:col-span-2">
          <h3 className="font-heading font-bold text-slate-900 mb-4 flex items-center gap-2"><CalendarCheck size={18} className="text-blue-600" />Attendance</h3>
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[180px]">
              <div className="flex items-center gap-6">
                <div><p className="text-xs text-slate-400">Check-in</p><p className="font-heading font-bold text-lg text-slate-900">{fmtTime(today?.check_in)}</p></div>
                <div><p className="text-xs text-slate-400">Check-out</p><p className="font-heading font-bold text-lg text-slate-900">{fmtTime(today?.check_out)}</p></div>
                <div><p className="text-xs text-slate-400">Present (mo)</p><p className="font-heading font-bold text-lg text-emerald-600">{act.attendance?.present_this_month || 0}d</p></div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => mark("check-in")} disabled={!!today?.check_in} className="bg-blue-600 text-white hover:bg-blue-700 font-semibold disabled:opacity-50" data-testid="check-in-btn"><SignIn size={16} className="mr-1.5" />Check In</Button>
              <Button onClick={() => mark("check-out")} disabled={!today?.check_in || !!today?.check_out} variant="outline" className="border-slate-300 bg-white font-semibold disabled:opacity-50" data-testid="check-out-btn"><SignOut size={16} className="mr-1.5" />Check Out</Button>
            </div>
          </div>
        </Panel>

        {isSales ? (
          <Panel className="p-5 flex items-center gap-4" data-testid="monthly-progress">
            <Ring percent={act.progress} />
            <div>
              <p className="text-xs text-slate-400">Monthly Progress</p>
              <p className="font-heading font-bold text-2xl text-slate-900">{act.progress}%</p>
              <p className="text-xs text-slate-400 mt-1">of your monthly goal</p>
              <p className="font-heading font-bold text-lg text-emerald-600 mt-1">{t.converted} deals won</p>
            </div>
          </Panel>
        ) : (
          <Panel className="p-5 flex items-center gap-4">
            <Clock size={40} className="text-blue-600" weight="duotone" />
            <div>
              <p className="text-xs text-slate-400">Days Present (mo)</p>
              <p className="font-heading font-bold text-2xl text-slate-900">{act.attendance?.present_this_month || 0}</p>
              <p className="text-xs text-slate-400 mt-1">Keep up the great work</p>
            </div>
          </Panel>
        )}
      </div>

      {isSales && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard label="Commission (mo)" value={money(act.commission_month)} sub={`${act.commission_rate}% rate`} icon={CurrencyDollar} accent="blue" delay={0} />
          <StatCard label="Commission (all)" value={money(act.commission_all)} icon={TrendUp} accent="green" delay={50} />
          <StatCard label="Open Leads" value={t.open || 0} sub="in progress" icon={Target} accent="violet" delay={100} />
          <StatCard label="Win Rate / Score" value={`${act.win_rate}%`} sub={`${t.converted} won`} icon={ChartLineUp} accent="amber" delay={150} />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {isSales ? (
          <Panel className="p-5" data-testid="lead-breakdown">
            <h3 className="font-heading font-bold text-slate-900 mb-4">Lead Breakdown</h3>
            <div className="space-y-3">
              {[
                { label: "Open / In-progress", v: t.open, c: "text-blue-600" },
                { label: "Converted", v: t.converted, c: "text-emerald-600" },
                { label: "Lost", v: t.lost, c: "text-slate-500" },
                { label: "Rejected", v: t.rejected, c: "text-red-500" },
              ].map((r) => (
                <div key={r.label} className="flex items-center justify-between border-b border-slate-100 pb-2">
                  <span className="text-sm text-slate-600">{r.label}</span>
                  <span className={`font-heading font-bold ${r.c}`}>{r.v || 0}</span>
                </div>
              ))}
            </div>
          </Panel>
        ) : (
          <Panel className="p-5">
            <h3 className="font-heading font-bold text-slate-900 mb-4 flex items-center gap-2"><Clock size={18} className="text-blue-600" />Recent Attendance</h3>
            {(!act.attendance?.recent || act.attendance.recent.length === 0) ? (
              <EmptyState icon={CalendarCheck} title="No records yet" hint="Check in to start tracking attendance." />
            ) : (
              <div className="divide-y divide-slate-100">
                {act.attendance.recent.map((a) => (
                  <div key={a.id} className="flex items-center justify-between py-2 text-sm">
                    <span className="text-slate-600 font-medium">{a.date}</span>
                    <span className="text-slate-500">{fmtTime(a.check_in)} → {fmtTime(a.check_out)}</span>
                    <CheckCircle size={16} weight="fill" className={a.check_in ? "text-emerald-500" : "text-slate-200"} />
                  </div>
                ))}
              </div>
            )}
          </Panel>
        )}

        <TicketsPanel />
        <MySalarySlips />
      </div>
    </div>
  );
}
